/**
 * Cloud-file connection status + connect flow.
 *
 * GET {connect_path}?redirect_path=<in-app path> returns { auth_url }; after
 * consent the provider callback sends the browser to FRONTEND_URL +
 * redirect_path, and posts no message back. Doing that in the main window would
 * throw away the user's draft, so we run it in a popup (redirect_path = the
 * current conversation) and poll the status endpoint until the provider reports
 * connected (or the popup closes). If the popup is blocked we fall back to a
 * same-window redirect back to this conversation and leave a sessionStorage
 * marker so the composer reopens the picker on return (see takePendingConnect).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { cloudFilesApi, CloudConnectProvider, CloudFilesStatus } from '@/services/cloudFilesApi';
import useActiveOrgId from '@/hooks/useActiveOrgId';

/**
 * Cloud-file state is per user + ORG (each org enables providers and holds its
 * own grants), so every cache key carries the active org id: switching org
 * refetches and never shows another org's connection.
 */
export const cloudStatusKey = (orgId: string) => ['cloud-files-status', orgId] as const;
export const cloudItemsKey = (orgId: string, query: unknown) => ['cloud-files-ms-items', orgId, query] as const;
export const cloudOrgSettingsKey = (orgId: string) => ['cloud-files-org-settings', orgId] as const;

const POLL_MS = 2000;
const PENDING_KEY = 'weavein.cloudConnectPending';

/** The in-app path to come back to after consent (must start with "/"). */
export function currentInAppPath(): string {
  const path = `${window.location.pathname || '/'}${window.location.search || ''}`;
  return path.startsWith('/') ? path : `/${path}`;
}

/**
 * After a same-window connect redirect: which provider the user was connecting
 * (and clears the marker), or null.
 */
export function takePendingConnect(): CloudConnectProvider | null {
  try {
    const v = sessionStorage.getItem(PENDING_KEY);
    if (!v) return null;
    sessionStorage.removeItem(PENDING_KEY);
    return v === 'google_drive' || v === 'microsoft_files' ? v : null;
  } catch {
    return null;
  }
}
const MAX_WAIT_MS = 5 * 60 * 1000;

export type CloudConnectErrorCode = 'org_disabled' | 'domain_not_allowed' | 'tenant_not_allowed' | 'unknown';

/**
 * The connect callback can come back with ?cloud_error=<code>[&provider=…].
 * Returns it (once) and strips both params from the URL so a refresh doesn't
 * repeat the message.
 */
export function takeCloudConnectError(): { code: CloudConnectErrorCode; provider: CloudConnectProvider | null } | null {
  if (typeof window === 'undefined') return null;
  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    return null;
  }
  const raw = url.searchParams.get('cloud_error');
  if (!raw) return null;
  const p = url.searchParams.get('provider');
  url.searchParams.delete('cloud_error');
  if (p === 'google_drive' || p === 'microsoft_files') url.searchParams.delete('provider');
  try {
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    /* ignore */
  }
  const code: CloudConnectErrorCode =
    raw === 'org_disabled' || raw === 'domain_not_allowed' || raw === 'tenant_not_allowed' ? raw : 'unknown';
  return { code, provider: p === 'google_drive' || p === 'microsoft_files' ? p : null };
}

export function useCloudFilesStatus(enabled = true) {
  const orgId = useActiveOrgId();
  return useQuery<CloudFilesStatus>({
    queryKey: cloudStatusKey(orgId),
    queryFn: () => cloudFilesApi.getStatus(),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

function isConnected(status: CloudFilesStatus | undefined, provider: CloudConnectProvider): boolean {
  return !!status?.[provider]?.connected;
}

export function useCloudConnect() {
  const queryClient = useQueryClient();
  const orgId = useActiveOrgId();
  const [connecting, setConnecting] = useState<CloudConnectProvider | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearInterval(timer.current);
    },
    [],
  );

  /** Resolves true once connected, false if the user gave up (popup closed / timeout). */
  const connect = useCallback(
    async (provider: CloudConnectProvider, connectPath?: string | null): Promise<boolean> => {
      setConnecting(provider);
      try {
        const path =
          connectPath ||
          queryClient.getQueryData<CloudFilesStatus>(cloudStatusKey(orgId))?.[provider]?.connect_path ||
          `/api/integrations/cloud-files/${provider}/connect`;
        const { auth_url } = await cloudFilesApi.connect(path, currentInAppPath());
        const popup = window.open(auth_url, 'weavein-cloud-connect', 'width=560,height=720');
        if (!popup) {
          try {
            sessionStorage.setItem(PENDING_KEY, provider);
          } catch {
            /* storage blocked: the user just re-opens the menu */
          }
          window.location.href = auth_url;
          return false;
        }
        const started = Date.now();
        return await new Promise<boolean>((resolve) => {
          const finish = async (ok?: boolean) => {
            if (timer.current) clearInterval(timer.current);
            timer.current = null;
            const fresh = await queryClient
              .fetchQuery({ queryKey: cloudStatusKey(orgId), queryFn: () => cloudFilesApi.getStatus(), staleTime: 0 })
              .catch(() => undefined);
            resolve(ok ?? isConnected(fresh, provider));
          };
          timer.current = setInterval(async () => {
            if (popup.closed || Date.now() - started > MAX_WAIT_MS) {
              void finish();
              return;
            }
            try {
              const status = await cloudFilesApi.getStatus();
              if (isConnected(status, provider)) {
                queryClient.setQueryData(cloudStatusKey(orgId), status);
                try {
                  popup.close();
                } catch {
                  /* cross-origin close can throw in some webviews */
                }
                void finish(true);
              }
            } catch {
              /* keep polling */
            }
          }, POLL_MS);
        });
      } finally {
        setConnecting(null);
      }
    },
    [queryClient, orgId],
  );

  return { connect, connecting };
}
